import React, { useMemo, Children } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import { Button, ButtonGroup, ButtonToolbar } from 'reactstrap';
import cls from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStepBackward, faStepForward } from '@fortawesome/free-solid-svg-icons';
import '../assignmentCalendar/AssignmentCalendar.scss';

const localizer = momentLocalizer(moment);

const CustomToolbar = (props) => {
    const { label, onNavigate, onView, view } = props; // Destructure view here

    const goToPrevious = () => onNavigate('PREV');
    const goToToday = () => onNavigate('TODAY');
    const goToNext = () => onNavigate('NEXT');

    const handleViewChange = (viewName) => {
        onView(viewName);
    };

    return (
        <ButtonToolbar className="rbc-toolbar d-flex justify-content-between align-items-center mb-2">
            {/* Left side: Today / Back / Next */}
            <ButtonGroup size="md">
                <Button color="outline-primary p-1" onClick={goToPrevious}>
                    <FontAwesomeIcon icon={faStepBackward} />
                </Button>
                <Button color="outline-primary p-1" onClick={goToToday}>
                    <small>TODAY</small>
                </Button>
                <Button color="outline-primary p-1" onClick={goToNext}>
                    <FontAwesomeIcon icon={faStepForward} />
                </Button>
            </ButtonGroup>

            {/* Center: Current label */}
            <div className="rbc-toolbar-label text-center flex-grow-1">
                <strong>{label}</strong>
            </div>

            {/* Right side: Month / Week / Day / Agenda with active styling */}
            <ButtonGroup size="md">
                <Button
                    color="outline-secondary"
                    onClick={() => handleViewChange('month')}
                    active={view === 'month'}  // Highlight if active
                >
                    Month
                </Button>
                <Button
                    color="outline-secondary"
                    onClick={() => handleViewChange('week')}
                    active={view === 'week'}
                >
                    Week
                </Button>
                <Button
                    color="outline-secondary"
                    onClick={() => handleViewChange('day')}
                    active={view === 'day'}
                >
                    Day
                </Button>
                <Button
                    color="outline-secondary"
                    onClick={() => handleViewChange('agenda')}
                    active={view === 'agenda'}
                >
                    Agenda
                </Button>
            </ButtonGroup>
        </ButtonToolbar>
    );
};


const CustomEvent = ({ event }) => {
    return (
        <div className={cls('custom-event', event.isCancelled && 'cancelled')}>
            <div className="event-content">
                <span className="event-time">{moment(event.start).format('hh:mm A')}</span>
                <span className="event-title">{` - ${event.title}`}</span>
            </div>
        </div>
    );
};


const CURRENT_DATE = moment();

const ColoredDateCellWrapper = ({ children, value }) => React.cloneElement(Children.only(children), {
    style: {
        ...children.style,
        backgroundColor:
            CURRENT_DATE.isAfter(moment(value), 'day')
                ? '#ddd'
                : CURRENT_DATE.isSame(moment(value), 'day')
                    ? '#c0e1fa'
                    : 'white',
    },
});

const JobScheduler = ({ onDateSelect, project = {}, onJobSelect }) => {
    const events = useMemo(() => {
        const calendarEvents = [];
        project.jobs?.forEach((job, index) => {
            const jobId = job.id || job.tempSessionId;
            if (!job.jobDate) {
                console.warn(`Skipping job ${jobId} - No jobDate available.`);
                return;
            }

            const eventDate = new Date(job.jobDate);

            calendarEvents.push({
                title: job?.id ? `${job.jobNumber}` : `${job.contract.number}-${index + 1}`,
                start: eventDate,
                end: eventDate,
                jobId,
                jobData: job,
                isCancelled: job.isCancelled,
            });

        });

        return calendarEvents;
    }, [project]);

    return (
        <div className="jobSchedulerCalendar">
            <Calendar
                selectable
                localizer={localizer}
                events={events}
                startAccessor="start"
                endAccessor="end"
                style={{ height: 'auto', minHeight: '755px' }}
                onSelectSlot={({ start }) => onDateSelect(start)}
                onSelectEvent={(event) => onJobSelect(event.jobData, event.start)}
                components={{ toolbar: CustomToolbar, event: CustomEvent, dateCellWrapper: ColoredDateCellWrapper }}
                defaultView="month"
                views={['month', 'week', 'day', 'agenda']}
                formats={{
                    // Return an empty string so RBC doesn't show "9:43 AM – 9:43 AM"
                    eventTimeRangeFormat: () => '',
                }}
            />
        </div>
    );
};

export default JobScheduler;
